<template>
  <div
    class="flex md:flex-row flex-col max-w-[1200px] mx-auto gap-4 border-[1px] border-gray-200 rounded-lg p-8 bg-white"
  >
    <!-- Left Column: Info and Notes -->
    <div class="flex-1 space-y-4">
      <!-- Task Info -->
      <div class="space-y-1 flex flex-col gap-2">
        <span class="flex items-center mb-2">
          <CircleCheckBig
            v-if="item.status === 'DONE'"
            class="w-5 h-5 mr-2 mt-0.5 text-green-500"
          />
          <h2 class="text-gray-900 font-semibold">{{ item.path_step.task.name }}</h2>
          <span
            v-if="item.path_step.is_required"
            class="text-xs bg-cosmicPurple/20 text-cosmicPurple ml-2 px-2 py-0.5 rounded-full"
            >Required</span
          >
          <span
            v-if="!item.path_step.is_required"
            class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full"
            >Optional</span
          >
        </span>

        <span
          v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
          class="text-sm flex items-start"
        >
          <span class="font-semibold min-w-32 flex items-center">
            <SquareArrowOutUpRight class="w-4 h-4 mr-2" />
            Link:
          </span>
          <a
            :href="item.path_step.task.url"
            target="_blank"
            class="text-sm hover:text-cosmicPurple transition-all duration-200"
          >
            {{ item.path_step.task.url }}
          </a>
        </span>

        <p v-if="item.path_step.task.section?.name" class="text-sm flex items-start">
          <span class="flex items-center min-w-32">
            <component
              v-if="getSectionIcon(item.path_step.task.section.name)"
              :is="getSectionIcon(item.path_step.task.section.name)"
              class="w-4 h-4 mr-2"
            />
            <TableOfContents v-else class="w-4 h-4 mr-2 rotate-180" />
            <span class="text-gray-700 mr-2 font-semibold">Section:</span>
          </span>
          <span class="text-gray-900">{{ item.path_step.task.section?.name }}</span>
        </p>
        <p v-if="item.path_step.points" class="text-sm flex items-start">
          <span class="flex items-center min-w-32">
            <Info class="w-4 h-4 mr-2" />
            <span class="text-gray-700 mr-2 font-semibold">Points:</span>
          </span>
          <span class="flex items-center gap-1">
            <img src="/ot_coin.svg" class="h-4 w-4" />
            <span class="block text-gray-900">{{ item.path_step.points }}</span>
          </span>
        </p>
        <p v-if="item.path_step.task.details" class="text-sm flex items-start">
          <span class="flex items-center min-w-32">
            <Info class="w-4 h-4 mr-2" />
            <span class="text-gray-700 mr-2 font-semibold">Details:</span>
          </span>
          <span class="block text-gray-900">
            {{ item.path_step.task.details }}
          </span>
        </p>
        <p
          v-if="item.path_step.task.url && !isUrl(item.path_step.task.url)"
          class="text-sm flex items-start ml-32 text-gray-900"
        >
          {{ item.path_step.task.url }}
        </p>
      </div>

      <!-- Notes Section -->
      <div>
        <span class="flex items-center gap-2">
          <p class="text-sm font-medium text-gray-700">Notes:</p>
          <Loader2 v-if="isNotesLoading" class="w-4 h-4 animate-spin" />
        </span>
        <!-- Editable Notes (Not in Manage View) -->
        <div v-if="!isManageView">
          <textarea
            v-model="editingNotes"
            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
            rows="4"
            placeholder="Add notes here..."
            @click.stop="handleNotesInteraction"
            :disabled="isNotesLoading"
          ></textarea>
          <div v-if="notesInteractionState" class="flex justify-end items-center mt-2 space-x-2">
            <button
              @click.stop="saveNotes"
              class="bg-blue-500 text-white px-4 py-1 text-sm rounded-md disabled:opacity-50 flex items-center"
              :disabled="isNotesLoading || editingNotes === originalNotes"
            >
              <Loader2 v-if="isNotesLoading" class="w-4 h-4 mr-2 animate-spin" />
              Save
            </button>
          </div>
        </div>
        <!-- Read-only Notes (Manage View) -->
        <p
          v-else-if="isManageView && (editingNotes || (personalNotes && personalNotes?.notes))"
          class="mt-1 block w-full rounded-md p-2 text-sm bg-gray-50 text-gray-700 min-h-[100px] whitespace-pre-wrap"
        >
          {{ editingNotes || personalNotes?.notes }}
        </p>
        <p v-else-if="isManageView" class="mt-1 text-sm text-gray-500 italic">No notes added.</p>
      </div>
    </div>

    <!-- Right Column: Buttons -->
    <div class="w-[25%] flex-shrink-0 flex flex-col space-y-2">
      <a
        v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
        :href="item.path_step.task.url"
        target="_blank"
        class="w-full bg-cosmicPurple text-white px-4 py-2 rounded-md flex items-center justify-center text-sm hover:-translate-y-[1px] transition-transform duration-200 hover:shadow-md max-w-48 ml-auto"
        @click.prevent="props.handleOpenTask ? props.handleOpenTask(item) : openTaskUrl()"
      >
        <ExternalLink class="w-4 h-4 mr-2" />
        Open Task
      </a>

      <!-- Status Dropdown (Not in Manage View) -->
      <div v-if="!isManageView" class="relative">
        <Select
          v-model="currentStatus"
          @update:model-value="updateStatus"
          :disabled="isUpdating || localUpdating"
        >
          <SelectTrigger
            class="w-full"
            :class="[
              currentStatus === 'DONE'
                ? 'border-lime-500 text-lime-600 max-w-48 ml-auto'
                : currentStatus === 'IN-PROGRESS'
                  ? 'border-blue-500 text-blue-500 max-w-48 ml-auto'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400 max-w-48 ml-auto'
            ]"
          >
            <div class="flex items-center gap-2">
              <Loader2
                v-if="localUpdating || isUpdating || (pendingReview && isSubmittingReview)"
                class="w-4 h-4 animate-spin"
              />
              <template v-else>
                <CheckCircle v-if="currentStatus === 'DONE'" class="w-4 h-4" />
                <Clock v-else-if="currentStatus === 'IN-PROGRESS'" class="w-4 h-4" />
                <CircleDot v-else class="w-4 h-4" />
              </template>
              <SelectValue :placeholder="formatStatus(item.status)" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="TO START">
                <div class="flex items-center gap-2">
                  <CircleDot class="w-4 h-4" />
                  <span>Incomplete</span>
                </div>
              </SelectItem>
              <SelectItem value="IN-PROGRESS">
                <div class="flex items-center gap-2">
                  <Clock class="w-4 h-4" />
                  <span>In Progress</span>
                </div>
              </SelectItem>
              <SelectItem value="DONE">
                <div class="flex items-center gap-2">
                  <CheckCircle class="w-4 h-4" />
                  <span>Completed</span>
                </div>
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div v-if="item.path_step.task.fellow_reportable">
        <button 
          @click="showReportModal = true"
          class="w-full px-4 py-2 text-gray-700 border-[1px] border-gray-300 rounded-md flex items-center justify-center text-sm hover:-translate-y-[1px] transition-transform duration-200 hover:shadow-md max-w-48 ml-auto"
        >
          <MessageSquareWarning class="w-4 h-4 mr-2" />
          Report fellow issue
        </button>
      </div>
    </div>
  </div>

  <!-- Review Modal (Rendered directly, structure now inside TaskReview) -->
  <TaskReview
    v-if="pendingReview"
    :path-step-id="item.path_step.id"
    :task-id="item.path_step.task.id"
    :progress-id="item.id"
    :path-step-name="item.path_step.task.name"
    :on-complete="handleReviewComplete"
    :on-cancel="handleReviewCancel"
  />

  <!-- Fellow Report Modal -->
  <FellowReportModal
    :is-open="showReportModal"
    :progress-id="item.id"
    @close="showReportModal = false"
    @submitted="handleReportSubmitted"
  />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  ExternalLink,
  CheckCircle,
  Loader2,
  TableOfContents,
  Info,
  CircleCheckBig,
  Clock,
  CircleDot,
  SquareArrowOutUpRight,
  MessageSquareWarning
} from 'lucide-vue-next'
import { isUrl } from '@/utils/isUrl'
import type { PersonalNote, TableProgressItem, TaskStatus } from '@/types/curriculum'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import TaskReview from './TaskReview.vue'
import FellowReportModal from './FellowReportModal.vue'
import { useCurriculumStore } from '@/stores/curriculum'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'
import { getSectionIcon } from '@/const/curriculumSections'
import { useReviewStore } from '@/stores/review'

const props = defineProps<{
  item: TableProgressItem
  isNotesLoading: boolean
  isUpdating: boolean
  personalNotes: PersonalNote | null
  isManageView: boolean
  handleOpenTask?: (item: TableProgressItem, event?: Event) => Promise<void>
}>()

const emit = defineEmits<{
  (e: 'save-notes', notes: string): void
  (e: 'notes-interacted'): void
  (e: 'review-cancel'): void
  (e: 'review-complete'): void
}>()

const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const reviewStore = useReviewStore()
const route = useRoute()
const toast = useToast()

const editingNotes = ref(props.personalNotes?.notes || '')
const originalNotes = ref(props.personalNotes?.notes || '')
const notesInteractionState = ref(false)
const localUpdating = ref(false)
const currentStatus = ref<TaskStatus>(props.item.status)
const pendingReview = ref(false)
const isSubmittingReview = ref(false)
const showReportModal = ref(false)

// onMounted(async () => {
//   await reviewStore.fetchLatestReview(props.item.path_step.task.id)
// })

// Watch for changes to personalNotes and update the local refs
watch(
  () => props.personalNotes,
  (newNotes) => {
    if (newNotes?.notes) {
      editingNotes.value = newNotes.notes
      originalNotes.value = newNotes.notes
    }
  },
  { immediate: true }
)

const saveNotes = async () => {
  emit('save-notes', editingNotes.value)
  notesInteractionState.value = false
}

const performStatusUpdate = async (itemId: number, targetStatus: TaskStatus) => {
  localUpdating.value = true

  if (!expeditionsStore.latestExpeditionId) {
    console.error('Cannot update status, missing expedition ID')
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Cannot update status: Missing expedition ID.',
      life: 3000
    })
    localUpdating.value = false
    return
  }

  try {
    const weekIdFromRoute = route.query.week ? String(route.query.week) : null
    await curriculumStore.updateProgressStatus(
      itemId,
      targetStatus,
      expeditionsStore.latestExpeditionId,
      weekIdFromRoute
    )

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Progress status updated successfully',
      life: 3000
    })
    currentStatus.value = targetStatus // Reflect successful update
  } catch (err) {
    console.error('Error updating status in TaskExpanded:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update progress status',
      life: 5000
    })
    currentStatus.value = props.item.status // Revert on error
  } finally {
    localUpdating.value = false
  }
}

const updateStatus = async (newStatusValue: any) => {
  if (newStatusValue === null) return

  const status = newStatusValue as TaskStatus

  if (status === 'DONE' && props.item.path_step.task.review_required !== false) {
    pendingReview.value = true
    currentStatus.value = status // Update UI to show 'DONE' while review is pending
  } else {
    // For 'TO START' or 'IN-PROGRESS' or 'DONE' without review required
    await performStatusUpdate(props.item.id, status)
  }
}

const handleNotesInteraction = () => {
  notesInteractionState.value = true
  emit('notes-interacted')
}

const handleReviewCancel = () => {
  pendingReview.value = false
  isSubmittingReview.value = false
  currentStatus.value = props.item.status
  emit('review-cancel')
}

const handleReviewComplete = () => {
  isSubmittingReview.value = false
  pendingReview.value = false
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'TO START':
      return 'Incomplete'
    case 'IN-PROGRESS':
      return 'In Progress'
    case 'DONE':
      return 'Done'
    default:
      return status
  }
}

const openTaskUrl = () => {
  const taskUrl = props.item.path_step.task.url
  if (taskUrl && isUrl(taskUrl)) {
    window.open(taskUrl, '_blank')
  }
}

const handleReportSubmitted = () => {
  showReportModal.value = false
}
</script>

<style scoped>
/* Remove the gradual-grow animation since parent handles it */
</style>
